<template>
  <section>
    <v-toolbar>
      <v-toolbar-title class="headline d-flex">
        <v-icon class="pr-2 align-self-center" color="grey" large
          >mdi-account</v-icon
        >
        <span class="align-self-center">Members</span>
      </v-toolbar-title>
      <v-btn
        color="error"
        icon
        @click="confirmDelete = true"
        v-if="selectedMembers.length"
      >
        <v-icon>mdi-delete</v-icon>
      </v-btn>
      <v-spacer></v-spacer>

      <v-menu
        bottom
        left
        :close-on-click="!search"
        :close-on-content-click="false"
        :offset-y="false"
        v-if="membersTotal"
      >
        <template v-slot:activator="{ on }">
          <v-btn color icon v-on="on" title="Export">
            <v-icon>mdi-export</v-icon>
          </v-btn>
        </template>
        <v-toolbar flat dense>
          <!--<v-btn outlined class="mx-2" color="primary">Export Filtered</v-btn>-->
          <v-btn outlined class="mx-2" color="primary" :href="exportURL()"
            >Export All</v-btn
          >
          <v-btn
            outlined
            class="mx-2"
            color="primary"
            :href="exportURL('group_filtered')"
            v-if="groupFilter && groupFilter.length"
            >Export Group Filtered</v-btn
          >
        </v-toolbar>
      </v-menu>

      <v-menu
        bottom
        left
        :close-on-click="!search"
        :close-on-content-click="false"
        :offset-y="false"
      >
        <template v-slot:activator="{ on }">
          <v-btn color icon v-on="on">
            <v-icon :color="editableTable ? 'primary' : ''">mdi-pencil</v-icon>
          </v-btn>
        </template>
        <v-toolbar flat dense :min-width="400" :max-width="500">
          <v-checkbox
            v-model="editableTable"
            prepend-icon="mdi-pencil"
            class="mt-5"
            label="Editable table"
          ></v-checkbox>
        </v-toolbar>
      </v-menu>
      <v-menu
        bottom
        left
        :close-on-click="true || !groupFilter || !groupFilter.length"
        :close-on-content-click="false"
        :offset-y="false"
      >
        <template v-slot:activator="{ on }">
          <v-btn
            :color="verifyFilter !== 'All' || bannedFilter !== 'All' || statusFilter !== null || (groupFilter && groupFilter.length) ? 'primary' : ''"
            icon
            v-on="on"
          >
            <v-icon>mdi-filter</v-icon>
          </v-btn>
        </template>
        <v-toolbar flat dense :min-width="600" :max-width="600">
          <v-select
            class="mt-5"
            v-model="groupFilter"
            :items="groups"
            clearable
            multiple
            item-text="title"
            item-value="id"
            prepend-icon="mdi-filter"
            label="Group filter..."
            dense
            filled
            single-line
          ></v-select>
          <v-select
            v-model="verifyFilter"
            :items="verified"
            class="mt-5 ml-4"
            label="Verification filter..."
            dense
            filled
            single-line
          ></v-select>
          <v-select
            v-model="bannedFilter"
            :items="banned"
            class="mt-5 ml-4"
            label="Banned status filter..."
            dense
            filled
            single-line
          ></v-select>
          <v-select
            v-model="statusFilter"
            :items="memberStatusses"
            class="mt-5 ml-4"
            label="Member status filter..."
            dense
            filled
            clearable
            single-line
          ></v-select>
        </v-toolbar>
      </v-menu>
      <v-menu
        bottom
        left
        :close-on-click="!search"
        :close-on-content-click="false"
        :offset-y="false"
      >
        <template v-slot:activator="{ on }">
          <v-btn color icon v-on="on">
            <v-icon>mdi-magnify</v-icon>
          </v-btn>
        </template>
        <v-toolbar flat dense :min-width="400" :max-width="500">
          <v-text-field
            v-model="search"
            hide-details
            prepend-icon="mdi-magnify"
            flat
            clearable
            single-line
            filled
            placeholder="Search first/last name, cell number, email or address..."
          ></v-text-field>
        </v-toolbar>
      </v-menu>
    </v-toolbar>

    <Confirm
      :confirm.sync="confirmDelete"
      :callback="deleteMembers"
      :message="`Are you sure you want to <b>permanently delete</b> these ${selectedMembers.length} members?`"
    ></Confirm>

    <v-data-table
      v-model="selectedMembers"
      item-key="id"
      :items="members"
      :search="search"
      :headers="headers"
      :page.sync="page"
      :options.sync="options"
      :loading="membersLoading"
      :items-per-page.sync="itemsPerPage"
      :server-items-length="membersTotal"
      :footer-props="{ 'items-per-page-options': [10, 20, 50, 100] }"
      :multi-sort="true"
      :must-sort="true"
      :single-expand="true"
      :show-select="true"
      :single-select="false"
      :show-expand="false"
      @click:row="memberClick"
      class="elevation-1 mt-4"
    >
      <template v-slot:item.first_name="memberRow">
        <v-edit-dialog
          :return-value.sync="memberRow.item.first_name"
          @save="save(memberRow.item)"
          @cancel="cancel"
          @close="close"
          v-if="editableTable"
        >
          {{ memberRow.item.first_name }}
          <template v-slot:input>
            <v-text-field
              v-model="memberRow.item.first_name"
              label="Edit"
              single-line
              counter
            ></v-text-field>
          </template>
        </v-edit-dialog>
        <span v-else>{{ memberRow.item.first_name }}</span>
      </template>
      <template v-slot:item.last_name="memberRow">
        <v-edit-dialog
          :return-value.sync="memberRow.item.last_name"
          @save="save(memberRow.item)"
          @cancel="cancel"
          @close="close"
          v-if="editableTable"
        >
          {{ memberRow.item.last_name }}
          <template v-slot:input>
            <v-text-field
              v-model="memberRow.item.last_name"
              label="Edit"
              single-line
              counter
            ></v-text-field>
          </template>
        </v-edit-dialog>
        <span v-else>{{ memberRow.item.last_name }}</span>
      </template>
      <template v-slot:item.cell="memberRow">
        <v-edit-dialog
          :return-value.sync="memberRow.item.cell"
          @save="save(memberRow.item)"
          @cancel="cancel"
          @close="close"
          v-if="editableTable"
        >
          {{ memberRow.item.cell }}
          <template v-slot:input>
            <v-text-field
              v-model="memberRow.item.cell"
              label="Edit"
              single-line
              counter
            ></v-text-field>
          </template>
        </v-edit-dialog>
        <span v-else>{{ memberRow.item.cell }}</span>
      </template>
      <template v-slot:item.email="memberRow">
        <v-edit-dialog
          :return-value.sync="memberRow.item.email"
          @save="save(memberRow.item)"
          @cancel="cancel"
          @close="close"
          v-if="editableTable"
        >
          {{ memberRow.item.email }}
          <template v-slot:input>
            <v-text-field
              v-model="memberRow.item.email"
              label="Edit"
              single-line
              counter
            ></v-text-field>
          </template>
        </v-edit-dialog>
        <span v-else>{{ memberRow.item.email }}</span>
      </template>
      <template v-slot:item.member_tg_username="memberRow">
        <a v-if="memberRow.item.member_tg_username" :href="`https://t.me/${memberRow.item.member_tg_username}`" target="_blank">@{{ memberRow.item.member_tg_username }}</a>
        <span v-else>-</span>
      </template>
      <template v-slot:item.created_at="{ value }">
        <div dark>{{ dateFormat(value) }}</div>
      </template>
      <template v-slot:item.groups="{ value }">
        <v-chip dark>
          <span class="chip-label">{{ inGroups(value).length }}</span>
        </v-chip>
      </template>
      <template v-slot:item.verified="{ value }">
        <v-icon v-if="value">mdi-check</v-icon>
        <v-icon v-if="!value">mdi-close</v-icon>
      </template>
      <template v-slot:item.banned="{ value }">
        <v-icon v-if="value">mdi-check</v-icon>
        <v-icon v-if="!value">mdi-close</v-icon>
      </template>
    </v-data-table>
    <v-snackbar v-model="snack" :timeout="3000" :color="snackColor">
      {{ snackText }}
      <template v-slot:action="{ attrs }">
        <v-btn v-bind="attrs" text @click="snack = false">Close</v-btn>
      </template>
    </v-snackbar>

    <Member
      :member.sync="selectedMember"
      :member-statusses="memberStatusses"
      @on-close="selectedMembers = []"
    ></Member>
  </section>
</template>

<script>
import moment from "moment";
import { botClient } from "@/bot";
import { mapActions, mapGetters, mapState } from "vuex";
import { makeFindMixin, makeGetMixin } from "feathers-vuex";
import Confirm from "@/components/widget/Confirm";
import Member from "@/components/members/Member";

export default {
  data: () => ({
    snack: false,
    snackColor: "",
    snackText: "",
    selectedMembers: [],
    selectedMember: null,
    page: 1,
    pageCount: 0,
    itemsPerPage: 10,
    options: {},
    search: "",
    sort: {},
    memberQuery: {},
    groupFilter: null,
    verifyFilter: 'All',
    bannedFilter: 'All',
    statusFilter: null,
    editableTable: false,
    confirmDelete: false,
    verified: ['All', 'Verified Only', 'Unverified Only'],
    banned: ['All', 'Banned Only', 'Not Banned Only'],
    configId: "",
    headers: [
      { text: "First name", value: "first_name", divider: true },
      { text: "Last name", value: "last_name", divider: true },
      { text: "Cell", value: "cell", sortable: false, divider: true },
      {
        text: "Email",
        value: "email",
        sortable: false,
        filterable: false,
        divider: true,
      },
      { text: "Telegram", value: "member_tg_username" },
      { text: "Date", value: "created_at" },
      { text: "Groups", value: "groups", sortable: false, filterable: false },
      { text: "Verified", value: "verified", sortable: false, filterable: false },
      { text: "Banned", value: "banned", sortable: false, filterable: false },
      { text: "Status", value: "status", sortable: false, filterable: true },
    ],
  }),
  async mounted() {
    this.options = {
      sortBy: ["created_at"],
      sortDesc: [true],
    };

    setTimeout(() => {
      this.configId = "memberStatusses";
    }, 500);
  },
  mixins: [
    makeFindMixin({ service: "members", watch: true }),
    makeFindMixin({ service: "groups" }),
    makeGetMixin({
      service: "configuration",
      id: "configId",
      name: "memberStatussesConfig"
    }),
  ],
  computed: {
    ...mapGetters("config", ["botApiUrl"]),
    ...mapState("apiAuth", ["accessToken"]),
    membersLoading() {
      return this.isFindMembersPending;
    },
    membersTotal() {
      return (
        this.membersPaginationData.default &&
        this.membersPaginationData.default.mostRecent.total
      );
    },
    memberStatusses() {
      return this.memberStatussesConfig && this.memberStatussesConfig.config;
    },
    memberStatussesConfigParams() {
      return {
        query: {},
      };
    },
    membersParams() {
      let query = {
        $limit: this.itemsPerPage,
        $skip: (this.page - 1) * this.itemsPerPage,
        $sort: this.sort,
        $or: [
          { first_name: { $regex: this.search || "", $options: "i" } },
          { last_name: { $regex: this.search || "", $options: "i" } },
          { cell: { $regex: this.search || "", $options: "i" } },
          { email: { $regex: this.search || "", $options: "i" } },
          { member_tg_username: { $regex: this.search || "", $options: "i" } },
          { physical_address: { $regex: this.search || "", $options: "i" } },
        ],
      };

      if (this.groupFilter && this.groupFilter.length) {
        query["groups.id"] = {
          $in: this.groupFilter,
        };
      }

      if (this.verifyFilter && this.verifyFilter !== 'All') {
        query.verified = this.verifyFilter === 'Verified Only' ? true : { $ne: true }
      }
      if (this.bannedFilter && this.bannedFilter !== 'All') {
        query.banned = this.bannedFilter === 'Banned Only' ? true : { $ne: true }
      }
      if (this.statusFilter && this.statusFilter !== null) {
        query.status = this.statusFilter
      }

      return {
        query: query
      };
    },
    membersFetchParams() {
      let query = {
        $eager: "groups",
        $limit: this.itemsPerPage,
        $skip: (this.page - 1) * this.itemsPerPage,
        $sort: this.sort,
        $or: [
          { first_name: { $ilike: `%${this.search || ""}%` } },
          { last_name: { $ilike: `%${this.search || ""}%` } },
          { cell: { $ilike: `%${this.search || ""}%` } },
          { email: { $ilike: `%${this.search || ""}%` } },
          { member_tg_username: { $ilike: `%${this.search || ""}%` } },
          { physical_address: { $ilike: `%${this.search || ""}%` } },
        ],
      };

      if (this.groupFilter && this.groupFilter.length) {
        query.$joinRelation = "groups";
        query["groups.id"] = {
          $in: this.groupFilter,
        };
      }

      if (this.verifyFilter && this.verifyFilter !== 'All') {
        query.verified = this.verifyFilter === 'Verified Only' ? true : { $ne: true }
      }
      if (this.bannedFilter && this.bannedFilter !== 'All') {
        query.banned = this.bannedFilter === 'Banned Only' ? true : { $ne: true }
      }
      if (this.statusFilter && this.statusFilter !== null) {
        query.status = this.statusFilter
      }

      return {
        query: query,
        debounce: 700,
      };
    },
    groupsParams() {
      return {
        query: {
          $select: ["id", "title", "bot_managed"],
        },
      };
    },
  },
  methods: {
    ...mapActions("app", ["showMessage"]),
    exportURL(what = "all") {
      let exportUrl = new URL(
        botClient.service("members").path,
        this.botApiUrl || process.env.VUE_APP_API_URL
      ).toString();

      exportUrl = `${exportUrl}?$export=${what}&access_token=${this.accessToken}`;
      if (
        what === "group_filtered" &&
        this.groupFilter &&
        this.groupFilter.length
      )
        exportUrl += `&group_ids=${this.groupFilter.join(",")}`;

      return exportUrl;
    },
    dateFormat(date) {
      return moment(date).format("YYYY-MM-DD HH:mm");
    },
    memberClick(member) {
      this.selectedMember = member;
    },
    inGroups(groups) {
      return groups.map((group) => group.in_group);
    },
    save(member) {
      try {
        member.save();
        this.snack = true;
        this.snackColor = "success";
        this.snackText = "Member saved";
      } catch (error) {
        this.snack = true;
        this.snackColor = "error";
        this.snackText = "Member failed to save";
      }
    },
    cancel() {
      this.snack = true;
      this.snackColor = "error";
      this.snackText = "Canceled";
    },
    close() {
      console.log("Dialog closed");
    },
    async deleteMembers() {
      let memberIds = this.selectedMembers.map((member) => member.id);
      console.log(memberIds);
      try {
        await botClient
          .service("members")
          .remove(null, { query: { id: { $in: memberIds } } });

        await this.findMembers();
        this.selectedMembers = [];
      } catch (error) {
        this.showMessage({
          text: error.message || "Error occured!",
          color: "error",
        });
      }
    },
  },
  watch: {
    options(now, before) {
      if (now.sortBy !== before.sortBy && now.sortBy.length) {
        this.sort = {};
        now.sortBy.forEach((sorter, index) => {
          this.sort[sorter] = now.sortDesc[index] ? -1 : 1;
        });
      }
    },
  },
  components: {
    Member,
    Confirm,
  },
};
</script>

<style lang="scss" scoped>
.chip-label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 80px;
}
</style>